import {useEffect, useCallback} from 'react'
import {connect} from 'react-redux'
import {useScript} from '../../hooks'
import {formatError} from '../../utils'
import {checkIsEmailAvailableUsingToken} from '../../apis'
import {signInWithApple, formSubmittedWithErrors, showAlert} from '../../actions'


/**
 * mode: 'sign-in' | 'sign-up'
 * 
 * Props for both modes:
 *   - mode
 *   - setIsFetching(fn)
 *   - showAlert(message, variant)
 *   - formSubmittedWithErrors(errors)       // signin only
 * 
 * Additional props for signup mode:
 *   - signUpUsingApple(token)
 *   - setError(message)
 */
const AppleAuthButton = ({
  mode = 'sign-in',
  usePopup = true,
  setError,
  showAlert,
  setIsFetching,
  signInWithApple,
  signUpWithApple,
  formSubmittedWithErrors
}) => {
  const [isLoaded] = useScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
  )

  const handleSuccess = useCallback(
    async (event) => {
      const token = event.detail?.authorization?.id_token
      if (!token) {
        showAlert('Could not retrieve Apple token', 'error')
        setIsFetching(false)
        return
      }

      if (mode === 'sign-in') {
        // Sign in flow
        try {
          await signInWithApple(token)
        } catch (err) {
          const { messages } = formatError(err)
          formSubmittedWithErrors(messages)
        } finally {
          setIsFetching(false)
        }
      } else {
        // Signup flow: first check availability
        try {
          const { data: isAvailable } = await checkIsEmailAvailableUsingToken(token)
          if (!isAvailable) {
            setError?.('Email already signed up')
            return
          }
          await signUpWithApple(token)
        } catch (err) {
          showAlert('Something went wrong', 'error')
          setError?.('Something went wrong')
        } finally {
          setIsFetching(false)
        }
      }
    },
    [
      mode,
      setError,
      showAlert,
      setIsFetching,
      signInWithApple,
      signUpWithApple,
      formSubmittedWithErrors
    ]
  )


  const handleFailure = useCallback(
    (event) => {
      const error = event.detail?.error
      if (error && error !== 'popup_closed_by_user') {
        showAlert('Something went wrong during Apple auth', 'error')
      }
      setIsFetching(false)
    },
    [showAlert, setIsFetching]
  )


  // initialize AppleID and wire up listeners once the script is ready
  useEffect(() => {
    if (!isLoaded || !window.AppleID?.auth) return

    window.AppleID.auth.init({
      clientId:    process.env.REACT_APP_APPLE_CLIENT_ID,
      scope:       'email',
      usePopup:    usePopup,
      redirectURI: `${process.env.REACT_APP_BASE_URL}/${mode === 'sign-in' ? 'sign-in' : 'sign-up'}`,
    })

    document.addEventListener('AppleIDSignInOnSuccess', handleSuccess)
    document.addEventListener('AppleIDSignInOnFailure', handleFailure)
    return () => {
      document.removeEventListener('AppleIDSignInOnSuccess', handleSuccess)
      document.removeEventListener('AppleIDSignInOnFailure', handleFailure)
    }
  }, [isLoaded, mode, usePopup, handleSuccess, handleFailure])



  function handleClick() {
    setIsFetching(true)
  }



  return (
    <div
      id='appleid-signin'
      style={{cursor: 'pointer'}}
      data-type={mode}
      data-color='black'
      data-border='false'
      data-border-radius='50'
      data-height='44'
      onClick={handleClick}
    />
  )
}

const actions = {
  showAlert,
  signInWithApple,
  formSubmittedWithErrors
}

export default connect(null, actions)(AppleAuthButton)


